import { motion } from 'framer-motion';
import { Star, TrendingUp } from 'lucide-react';
import { useAppStore } from '../../store/useAppStore';

const LEVELS = [
  { level: 1, name: 'Newcomer', minXp: 0, maxXp: 100, color: 'from-gray-400 to-gray-500' },
  { level: 2, name: 'Bronze', minXp: 100, maxXp: 300, color: 'from-orange-600 to-orange-400' },
  { level: 3, name: 'Silver', minXp: 300, maxXp: 600, color: 'from-gray-300 to-gray-400' },
  { level: 4, name: 'Gold', minXp: 600, maxXp: 1000, color: 'from-yellow-500 to-yellow-400' },
  { level: 5, name: 'Platinum', minXp: 1000, maxXp: 1500, color: 'from-cyan-400 to-blue-400' },
  { level: 6, name: 'Diamond', minXp: 1500, maxXp: 2200, color: 'from-blue-400 to-purple-400' },
  { level: 7, name: 'Master', minXp: 2200, maxXp: 3000, color: 'from-purple-500 to-pink-500' },
  { level: 8, name: 'Grandmaster', minXp: 3000, maxXp: 4000, color: 'from-red-500 to-orange-500' },
  { level: 9, name: 'Legend', minXp: 4000, maxXp: 5500, color: 'from-yellow-400 to-red-500' },
  { level: 10, name: 'Mythic', minXp: 5500, maxXp: Infinity, color: 'from-purple-600 to-pink-600' },
];

export function LevelProgress() {
  const user = useAppStore((state) => state.user);

  if (!user) return null;

  const currentLevelData = LEVELS.find((l) => l.level === user.level) || LEVELS[0];
  const nextLevelData = LEVELS.find((l) => l.level === user.level + 1);

  const xpInCurrentLevel = user.experience - currentLevelData.minXp;
  const xpNeeded = nextLevelData 
    ? nextLevelData.minXp - currentLevelData.minXp 
    : 1000;
  const progressPercent = Math.min((xpInCurrentLevel / xpNeeded) * 100, 100);

  const multiplier = 1 + (user.level - 1) * 0.1;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="card p-4"
    >
      <div className="flex items-center justify-between mb-3">
        {/* Level badge */}
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${currentLevelData.color} flex items-center justify-center`}>
            <span className="text-xl font-bold">{user.level}</span>
          </div>
          <div>
            <p className="font-bold">{currentLevelData.name}</p>
            <p className="text-white/50 text-sm">Level {user.level}</p>
          </div>
        </div>

        {/* Multiplier */}
        <div className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-yellow-500/20 text-yellow-400">
          <TrendingUp className="w-4 h-4" />
          <span className="font-medium text-sm">{multiplier.toFixed(1)}x</span>
        </div>
      </div>

      {/* XP Progress bar */}
      <div className="mb-2">
        <div className="flex justify-between text-xs text-white/50 mb-1">
          <span>{user.experience.toLocaleString()} XP</span>
          {nextLevelData && (
            <span>{nextLevelData.minXp.toLocaleString()} XP</span>
          )}
        </div>
        <div className="h-2.5 bg-white/10 rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${progressPercent}%` }}
            transition={{ duration: 1, ease: 'easeOut' }}
            className={`h-full rounded-full bg-gradient-to-r ${currentLevelData.color}`}
          />
        </div>
      </div>

      {/* XP to next level */}
      {nextLevelData && (
        <p className="text-xs text-white/40 text-center">
          {(nextLevelData.minXp - user.experience).toLocaleString()} XP to {nextLevelData.name}
        </p>
      )}

      {!nextLevelData && (
        <p className="text-xs text-yellow-400 text-center flex items-center justify-center gap-1">
          <Star className="w-3 h-3" />
          Maximum Level Reached!
        </p>
      )}
    </motion.div>
  );
}
